import {
    AfterViewInit,
    Component,
    ElementRef,
    forwardRef,
    HostBinding,
    Input,
    OnInit,
    ViewChild,
} from '@angular/core';
import { AbstractControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { BaseInputComponent } from '@sharedComponents/form/base-input.component';
import { DefaultInputErrorStateMatcher } from '@sharedComponents/form/base-input.error-state-matcher';
import BigNumber from 'bignumber.js';

/**
 * Parses a typed number in German or English notation.
 * - "." and "," both present: the last one is the decimal separator, the other one groups
 *   thousands ("1.200,50", "1,200.50").
 * - only ",": one comma is the decimal separator ("125,25"); several commas are invalid.
 * - only ".": several dots group thousands ("1.000.000"). A single dot is a decimal point
 *   ("125.25"), except when exactly 3 digits follow a leading digit and the field has at most
 *   2 decimals – then it groups thousands ("1.000" → 1000). Fields with more (or unknown)
 *   decimals keep "1.000" as 1.
 * Thousands groups must have 3 digits. Invalid input returns null.
 */
export function parseLocalizedNumber(val: string | number, decimals?: number | null): number | null {
    if (typeof val === 'number') {
        return Number.isFinite(val) ? val : null;
    }

    const raw = (val ?? '').replace(/[^\d,.-]/g, '');
    const negative = raw.startsWith('-');
    const unsigned = negative ? raw.slice(1) : raw;

    if (!/\d/.test(unsigned) || unsigned.includes('-')) {
        return null;
    }

    const lastDot = unsigned.lastIndexOf('.');
    const lastComma = unsigned.lastIndexOf(',');
    let integerPart = unsigned;
    let fractionPart = '';
    let groupSeparator: string = null;

    if (lastDot >= 0 && lastComma >= 0) {
        const decimalIndex = Math.max(lastDot, lastComma);
        const decimalSeparator = unsigned[decimalIndex];
        integerPart = unsigned.slice(0, decimalIndex);
        fractionPart = unsigned.slice(decimalIndex + 1);
        groupSeparator = decimalSeparator === '.' ? ',' : '.';

        if (integerPart.includes(decimalSeparator)) {
            return null;
        }
    } else if (lastComma >= 0) {
        const parts = unsigned.split(',');

        if (parts.length > 2) {
            return null;
        }

        [integerPart, fractionPart] = parts;
    } else if (lastDot >= 0) {
        const parts = unsigned.split('.');
        const isSingleThousandsDot = parts.length === 2
            && parts[0].length > 0
            && parts[1].length === 3
            && decimals != null
            && decimals <= 2;

        if (parts.length > 2 || isSingleThousandsDot) {
            groupSeparator = '.';
        } else {
            [integerPart, fractionPart] = parts;
        }
    }

    if (groupSeparator) {
        const groups = integerPart.split(groupSeparator);

        if (!/^\d{1,3}$/.test(groups[0]) || groups.slice(1).some(group => !/^\d{3}$/.test(group))) {
            return null;
        }

        integerPart = groups.join('');
    }

    const result = parseFloat(`${negative ? '-' : ''}${integerPart || '0'}.${fractionPart || '0'}`);

    return Number.isFinite(result) ? result : null;
}

@Component({
    selector: 'app-input-number',
    templateUrl: './number-input.component.html',
    styleUrls: ['./number-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NumberInputComponent),
            multi: true
        },
        {
            provide: MatFormFieldControl,
            useExisting: NumberInputComponent
        }
    ],
    standalone: false
})
export class NumberInputComponent extends BaseInputComponent<number> implements OnInit, AfterViewInit {

    errorStateMatcher: DefaultInputErrorStateMatcher;

    @ViewChild('input') private inputRef: ElementRef<HTMLInputElement>;

    @Input() showArrowButtons = true;

    @Input() step = 1;

    @Input() min: number;

    @Input() max: number;

    @Input() align: 'l' | 'r' = 'l';

    @Input() decimals: number;

    /** Rundet auf `decimals`, zeigt aber keine Nullen am Ende (1 statt 1,0000) – für Mengen. */
    @Input() trimTrailingZeros = false;

    @Input() fc: AbstractControl;

    @HostBinding('tabIndex') tabIndex = -1;

    get hasArrows(): boolean {
        return !this.disabled && this.showArrowButtons;
    }

    private isEditing = false;

    /** Text and value at focus time: an unchanged text on blur keeps the value untouched. */
    private textAtFocus: string = null;

    private valueAtFocus: number = null;

    ngOnInit(): void {
        super.ngOnInit();

        if (this.ngControl) {
            this.errorStateMatcher = new DefaultInputErrorStateMatcher(this.ngControl);
        }
    }

    ngAfterViewInit(): void {
        this.syncInputDisplay();
    }

    public writeValue(inputValue: number | string | null): void {
        const normalized = inputValue == null || inputValue === ''
            ? null
            : typeof inputValue === 'number'
                ? inputValue
                : this.normalizeNumericValue(inputValue);

        super.writeValue(normalized);
        this.syncInputDisplay();
    }

    public setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;

        if (this.inputRef?.nativeElement) {
            this._renderer.setProperty(this.inputRef.nativeElement, 'disabled', isDisabled);
        }

        this.stateChanges.next(undefined);
    }

    onContainerClick(_event: MouseEvent): void {
        if (!this.disabled) {
            this.inputRef?.nativeElement?.focus();
        }
        this.onTouch();
    }

    public customOnInput(event: Event): void {
        const input = event.target as HTMLInputElement;
        const parsed = this.normalizeNumericValue(input.value);

        this._value = parsed;
        this.onChange(parsed);
        this.stateChanges.next(undefined);
    }

    public customOnChange(event: Event): void {
        const parsed = this.safeValue((event.target as HTMLInputElement).value);

        this._value = parsed;
        this.onChange(parsed);
        this.valueChange.emit(parsed);
    }

    public onInputFocus(event: FocusEvent): void {
        this.isEditing = true;

        const input = event.target as HTMLInputElement;
        const current = this._value ?? this.normalizeNumericValue(input.value);

        input.value = current == null ? '' : this.formatDecimal(current);
        this.textAtFocus = input.value;
        this.valueAtFocus = this._value;
    }

    public onInputBlur(event: FocusEvent): void {
        this.isEditing = false;

        const input = event.target as HTMLInputElement;

        if (input.value === this.textAtFocus) {
            // Not edited: keep the (programmatically set) value, no rounding to `decimals`.
            if (this._value !== this.valueAtFocus) {
                this._value = this.valueAtFocus;
                this.onChange(this._value);
            }
        } else {
            this._value = this.safeValue(input.value);
            this.onChange(this._value);
        }

        input.value = this.formatDisplayValue(this._value);
        this.onTouch();
    }

    public incrementValue(event: Event): void {
        this.disableEvent(event);

        if (this._value == null || this.max == null || this._value < this.max) {
            this._value = new BigNumber(this._value || 0).plus(this.step).toNumber();
            this.onChange(this._value);
            this.valueChange.emit(this._value);
            this.syncInputDisplay();
        }
    }

    public decrementValue(event: MouseEvent): void {
        this.disableEvent(event);

        if (this._value == null || this.min == null || this._value > this.min) {
            this._value = new BigNumber(this._value || 0).minus(this.step).toNumber();
            this.onChange(this._value);
            this.valueChange.emit(this._value);
            this.syncInputDisplay();
        }
    }

    public disableEvent(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    private syncInputDisplay(): void {
        if (this.isEditing) {
            // Programmatic change while focused becomes the new untouched value.
            this.valueAtFocus = this._value;
            return;
        }

        if (!this.inputRef?.nativeElement) {
            return;
        }

        this.inputRef.nativeElement.value = this.formatDisplayValue(this._value);
    }

    private safeValue(val: string | number): number | null {
        const safeValue = this.normalizeNumericValue(val);

        if (safeValue == null) {
            return null;
        }

        if (this.decimals != null) {
            return new BigNumber(safeValue).decimalPlaces(this.decimals, BigNumber.ROUND_HALF_UP).toNumber();
        }

        return safeValue;
    }

    private normalizeNumericValue(val: string | number): number | null {
        return parseLocalizedNumber(val, this.decimals);
    }

    private formatDecimal(val: number | string): string {
        const numeric = typeof val === 'number' ? val : Number(String(val).replace(',', '.'));

        if (!Number.isFinite(numeric)) {
            return '';
        }

        if (this.decimals != null) {
            // Decimal half-up like the lists' DecimalPipe; Number#toFixed rounds the binary double (1.005 → "1.00").
            const fixed = new BigNumber(numeric).toFixed(this.decimals, BigNumber.ROUND_HALF_UP);
            const trimmed = this.trimTrailingZeros && fixed.includes('.')
                ? fixed.replace(/0+$/, '').replace(/\.$/, '')
                : fixed;
            return trimmed.replace('.', ',');
        }

        return this.stringify(numeric).replace('.', ',');
    }

    private formatDisplayValue(val: number | null): string {
        if (val == null) {
            return '';
        }

        return this.formatDecimal(val);
    }

    private stringify(val: string | number): string {
        return val == null ? '' : `${val}`;
    }

}
